// ─── RevealTab ───────────────────────────────────────────────
// Dashboard section: Morning After Reveal
// Status + Preview + Send/Open now

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Heart, Image as ImageIcon, ExternalLink, Copy, Check, Upload, X, MessageCircle, Mail, UserPlus, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const FRONTEND = window.location.origin;

export default function RevealTab({ eventId, token, adminDarkMode, onAddForGuest }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [ogPreviewKey, setOgPreviewKey] = useState(0); // force-refresh OG preview image
  const [askOpen, setAskOpen] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/events/${eventId}/reveal-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch { /* */ }
    setLoading(false);
  }, [eventId, token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Load current event to see if there's a custom OG cover set
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/events/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCoverUrl(res.data?.reveal_og_cover_url || null);
      } catch { /* */ }
    })();
  }, [eventId, token]);

  const handleSendNow = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/api/events/${eventId}/reveal-send`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchStatus();
    } catch { /* */ }
    setSending(false);
  };

  const handleCopyLink = async () => {
    if (!status?.reveal_token) return;
    // Prefer friendly URL (e.g. /sofia-miguel/morning-reveal/{token}) when slug is available
    const url = status.event_slug
      ? `${FRONTEND}/${status.event_slug}/morning-reveal/${status.reveal_token}`
      : `${API}/api/r/${status.reveal_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch { /* */ }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      alert('Image too large (max 8MB)');
      return;
    }
    setCoverUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/api/events/${eventId}/reveal-og-cover`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCoverUrl(res.data?.url || null);
      setOgPreviewKey(k => k + 1);
    } catch { /* */ }
    setCoverUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCoverRemove = async () => {
    try {
      await axios.delete(`${API}/api/events/${eventId}/reveal-og-cover`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCoverUrl(null);
      setOgPreviewKey(k => k + 1);
    } catch { /* */ }
  };

  const handleUpdateTime = async (time) => {
    try {
      await axios.put(`${API}/api/events/${eventId}/reveal-time`, { time }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(prev => ({ ...prev, scheduled_time: time }));
      setShowTimePicker(false);
    } catch { /* */ }
  };

  const handlePreview = () => {
    if (!status?.reveal_token) return;
    window.open(`${FRONTEND}/reveal/${status.reveal_token}?preview=1`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!status) return null;

  const hasContent = status.love_count > 0 || status.moments_count > 0;
  const isReady = status.love_count >= 3 || status.moments_count >= 5;

  return (
    <div className="max-w-md mx-auto py-8 space-y-8">
      {/* ─── Unified Morning Reveal landing card ─────────────── */}
      <div
        className="rounded-2xl border p-6 text-center"
        style={{
          backgroundColor: adminDarkMode ? '#0f172a' : '#fff',
          borderColor: adminDarkMode ? '#1e293b' : '#e2e8f0',
        }}
        data-testid="morning-reveal-landing"
      >
        <h2
          className="font-light tracking-wide text-2xl mb-2"
          style={{ color: adminDarkMode ? '#e2e8f0' : '#0f172a', fontFamily: 'serif' }}
        >
          Morning reveal
        </h2>
        <p className="text-[13px] italic mb-7" style={{ color: adminDarkMode ? '#94a3b8' : '#64748b' }}>
          Gather the moments they&rsquo;ll wake up to.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => setAskOpen(true)}
            className="px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
            style={{ backgroundColor: '#7c3aed', color: 'white' }}
            data-testid="reveal-ask-guests-btn"
          >
            <Share2 className="h-4 w-4" />
            Ask guests
          </button>
          <button
            onClick={() => onAddForGuest?.()}
            className="px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            style={{
              backgroundColor: adminDarkMode ? '#1e293b' : '#f1f5f9',
              color: adminDarkMode ? '#e2e8f0' : '#475569',
              border: `1px solid ${adminDarkMode ? '#334155' : '#cbd5e1'}`,
            }}
            data-testid="reveal-add-for-guest-btn"
          >
            <UserPlus className="h-4 w-4" />
            Add for a guest
          </button>
        </div>
      </div>

      {/* Content status — reassurance only */}
      <div
        className="p-5 rounded-xl border space-y-3"
        style={{
          backgroundColor: adminDarkMode ? '#0f172a' : '#fff',
          borderColor: adminDarkMode ? '#1e293b' : '#e2e8f0',
        }}
      >
        <div className="flex items-center gap-3">
          <Heart className="w-4 h-4" style={{ color: adminDarkMode ? '#94a3b8' : '#64748b' }} />
          <span className="text-sm" style={{ color: adminDarkMode ? '#cbd5e1' : '#475569' }}>
            {status.love_count} {status.love_count === 1 ? 'message' : 'messages'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ImageIcon className="w-4 h-4" style={{ color: adminDarkMode ? '#94a3b8' : '#64748b' }} />
          <span className="text-sm" style={{ color: adminDarkMode ? '#cbd5e1' : '#475569' }}>
            {status.moments_count} {status.moments_count === 1 ? 'moment captured' : 'moments captured'}
          </span>
        </div>

        {!hasContent && (
          <p className="text-xs pt-2" style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}>
            Not enough contributions yet
          </p>
        )}

        {hasContent && !isReady && (
          <p className="text-xs pt-2" style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}>
            A few more moments would make this special
          </p>
        )}
      </div>

      {/* Preview */}
      {hasContent && (
        <button
          onClick={handlePreview}
          className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          style={{
            backgroundColor: adminDarkMode ? '#1e293b' : '#f1f5f9',
            color: adminDarkMode ? '#cbd5e1' : '#475569',
          }}
          data-testid="reveal-preview-btn"
        >
          <ExternalLink className="w-4 h-4" />
          Preview the reveal &rarr;
        </button>
      )}

      {/* Send / Status — always show time picker + send action; 'sent' is a status line on top */}
      <div className="text-center space-y-3">
        {status.is_sent && (
          <div className="flex items-center justify-center gap-2">
            <Check className="w-3.5 h-3.5" style={{ color: adminDarkMode ? '#86efac' : '#16a34a' }} />
            <p className="text-sm" style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}>
              Sent{status.sent_at ? ` · ${new Date(status.sent_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : ''}
            </p>
          </div>
        )}

        {hasContent && (
          <>
            <div className="flex items-center justify-center gap-2">
              <p className="text-xs" style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}>
                {status.is_sent ? 'Auto-send time' : 'Scheduled for tomorrow at'} {status.scheduled_time || '10:30'}
              </p>
              <button
                onClick={() => setShowTimePicker(!showTimePicker)}
                className="text-[10px] underline underline-offset-2"
                style={{ color: adminDarkMode ? '#475569' : '#94a3b8' }}
                data-testid="reveal-change-time-btn"
              >
                change
              </button>
            </div>

            {showTimePicker && (
              <div className="flex flex-wrap justify-center gap-1.5 py-2" data-testid="reveal-time-picker">
                {['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00'].map(t => (
                  <button
                    key={t}
                    onClick={() => handleUpdateTime(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                      (status.scheduled_time || '10:30') === t ? 'font-medium' : ''
                    }`}
                    style={{
                      backgroundColor: (status.scheduled_time || '10:30') === t
                        ? (adminDarkMode ? '#334155' : '#e2e8f0')
                        : (adminDarkMode ? '#1e293b' : '#f8fafc'),
                      color: adminDarkMode ? '#cbd5e1' : '#475569',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}

            {!(hasContent && (status.love_count >= 4 || status.moments_count >= 4)) && (
              <p className="text-[11px]" style={{ color: adminDarkMode ? '#475569' : '#94a3b8', opacity: 0.7 }}>
                Not much has been shared yet.
              </p>
            )}

            <button
              onClick={handleSendNow}
              disabled={sending}
              className="w-full py-3 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
              style={{
                backgroundColor: adminDarkMode ? '#e2e8f0' : '#1e293b',
                color: adminDarkMode ? '#0f172a' : '#fff',
              }}
              data-testid="reveal-send-now"
            >
              {sending
                ? (status.is_sent ? 'Resending…' : 'Opening…')
                : (status.is_sent ? 'Resend now →' : 'Open now →')}
            </button>

            {status.is_sent && (
              <button
                onClick={handleCopyLink}
                className="w-full py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                style={{
                  backgroundColor: adminDarkMode ? '#1e293b' : '#f1f5f9',
                  color: adminDarkMode ? '#cbd5e1' : '#475569',
                }}
                data-testid="reveal-copy-link"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            )}
            {status.is_sent && (
              <p className="text-xs" style={{ color: adminDarkMode ? '#475569' : '#94a3b8' }}>
                Share it with anyone you&rsquo;d like
              </p>
            )}
          </>
        )}
      </div>

      {/* Share preview — editorial OG card (shown on WhatsApp / iMessage / etc.) */}
      {status?.reveal_token && (
        <div
          className="pt-6 mt-2 border-t space-y-3"
          style={{ borderColor: adminDarkMode ? '#1e293b' : '#e2e8f0' }}
          data-testid="reveal-og-preview-section"
        >
          <div className="flex items-baseline justify-between">
            <p className="text-xs tracking-wide" style={{ color: adminDarkMode ? '#94a3b8' : '#64748b' }}>
              Share preview
            </p>
            <p className="text-[10px]" style={{ color: adminDarkMode ? '#475569' : '#94a3b8' }}>
              How your link looks when shared
            </p>
          </div>

          <div
            className="rounded-lg overflow-hidden"
            style={{
              backgroundColor: adminDarkMode ? '#0f172a' : '#f8fafc',
              border: `1px solid ${adminDarkMode ? '#1e293b' : '#e2e8f0'}`,
            }}
          >
            <img
              key={ogPreviewKey}
              src={`${API}/api/reveal/${status.reveal_token}/og.jpg?v=${ogPreviewKey}`}
              alt="Share preview"
              className="w-full block"
              style={{ aspectRatio: '1200/630', objectFit: 'cover' }}
              data-testid="reveal-og-preview-image"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleCoverUpload}
              className="hidden"
              data-testid="reveal-og-cover-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={coverUploading}
              className="text-xs flex items-center gap-1.5 transition-opacity hover:opacity-70 disabled:opacity-40"
              style={{ color: adminDarkMode ? '#cbd5e1' : '#475569' }}
              data-testid="reveal-og-cover-upload"
            >
              <Upload className="w-3 h-3" />
              {coverUploading ? 'Uploading…' : (coverUrl ? 'Change cover' : 'Upload custom cover')}
            </button>
            {coverUrl && (
              <button
                onClick={handleCoverRemove}
                className="text-xs flex items-center gap-1 transition-opacity hover:opacity-70"
                style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}
                data-testid="reveal-og-cover-remove"
              >
                <X className="w-3 h-3" />
                Remove
              </button>
            )}
            <a
              href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(status.event_slug ? `${FRONTEND}/${status.event_slug}/morning-reveal/${status.reveal_token}` : `${API}/api/r/${status.reveal_token}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs ml-auto transition-opacity hover:opacity-70"
              style={{ color: adminDarkMode ? '#64748b' : '#94a3b8' }}
              data-testid="reveal-og-debug-link"
              title="Opens Meta's Sharing Debugger in a new tab"
            >
              Test preview &rarr;
            </a>
          </div>
          <p className="text-[11px] leading-relaxed" style={{ color: adminDarkMode ? '#475569' : '#94a3b8' }}>
            {coverUrl
              ? 'Your custom image is used for the share preview.'
              : 'Auto-selected from your guests\u2019 moments. Upload a custom image to override.'}
          </p>
        </div>
      )}

      {/* ─── Saved moments (read-only archive) ──────────────── */}
      {status?.kept_count > 0 && (
        <KeepsakesPanel eventId={eventId} token={token} adminDarkMode={adminDarkMode} />
      )}

      {/* ─── Ask guests share panel ──────────────────────────── */}
      {askOpen && (
        <AskGuestsModal
          eventSlug={status?.event_slug}
          eventName={status?.event_name}
          adminDarkMode={adminDarkMode}
          onClose={() => setAskOpen(false)}
        />
      )}
    </div>
  );
}

// ─── AskGuestsModal ────────────────────────────────────────────
// Quiet share panel — copy the guest-facing public link, share via WhatsApp,
// or hand off to email. Goal: get the link into one of the planner's existing
// communication channels with one tap. No accounts required for guests.
function AskGuestsModal({ eventSlug, eventName, adminDarkMode, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!eventSlug) return null;
  const guestUrl = `${FRONTEND}/event/${eventSlug}`;
  const shareText = `${eventName ? `${eventName} — ` : ''}Leave something for them: a note, voice message, or photo.`;
  const fullMessage = `${shareText}\n\n${guestUrl}`;

  const c = {
    text: adminDarkMode ? 'white' : '#0f172a',
    muted: adminDarkMode ? '#94a3b8' : '#64748b',
    faint: adminDarkMode ? '#64748b' : '#94a3b8',
    border: adminDarkMode ? '#1e293b' : '#e2e8f0',
    cardBg: adminDarkMode ? '#0f172a' : 'white',
    rowBg: adminDarkMode ? '#020617' : '#f8fafc',
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch { /* */ }
  };
  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(fullMessage)}`, '_blank');
  };
  const handleEmail = () => {
    const subject = eventName ? `Leave something for ${eventName}` : 'Leave something for them';
    const body = `${shareText}\n\n${guestUrl}\n\nNo account needed — just tap the link.`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="ask-guests-modal"
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl"
        style={{ backgroundColor: c.cardBg, border: `1px solid ${c.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-base font-medium" style={{ color: c.text }}>Invite guests to share</h3>
          <button onClick={onClose} className="opacity-50 hover:opacity-90 transition-opacity" data-testid="ask-guests-close">
            <X className="h-4 w-4" style={{ color: c.muted }} />
          </button>
        </div>
        <p className="text-[12px] px-6 pb-5 italic" style={{ color: c.faint }}>
          They can leave a note, a voice message, or a photo.
        </p>

        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={handleCopy}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors hover:opacity-90"
            style={{ borderColor: c.border, backgroundColor: c.rowBg, color: c.text }}
            data-testid="ask-guests-copy-link"
          >
            {copied ? <Check className="h-4 w-4" style={{ color: '#22c55e' }} /> : <Copy className="h-4 w-4" style={{ color: c.muted }} />}
            <span className="text-sm font-medium">{copied ? 'Link copied' : 'Copy link'}</span>
            <span className="ml-auto text-[11px] truncate max-w-[120px]" style={{ color: c.faint }}>
              {guestUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </span>
          </button>

          <button
            onClick={handleWhatsApp}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors hover:opacity-90"
            style={{ borderColor: c.border, backgroundColor: c.rowBg, color: c.text }}
            data-testid="ask-guests-whatsapp"
          >
            <MessageCircle className="h-4 w-4" style={{ color: '#25D366' }} />
            <span className="text-sm font-medium">Share on WhatsApp</span>
            <ExternalLink className="ml-auto h-3.5 w-3.5" style={{ color: c.faint }} />
          </button>

          <button
            onClick={handleEmail}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors hover:opacity-90"
            style={{ borderColor: c.border, backgroundColor: c.rowBg, color: c.text }}
            data-testid="ask-guests-email"
          >
            <Mail className="h-4 w-4" style={{ color: c.muted }} />
            <span className="text-sm font-medium">Send by email</span>
            <ExternalLink className="ml-auto h-3.5 w-3.5" style={{ color: c.faint }} />
          </button>
        </div>

        <p className="px-6 pb-6 text-[11px] italic text-center" style={{ color: c.faint }}>
          No accounts required for guests.
        </p>
      </div>
    </div>
  );
}

// ─── KeepsakesPanel ────────────────────────────────────────
// Read-only archive of "Your keeps" items the couple pinned during the reveal.
// Lets the couple re-download the book if they ever lose the reveal email.
function KeepsakesPanel({ eventId, token, adminDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/events/${eventId}/keepsakes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch { /* */ }
      setLoading(false);
    })();
  }, [eventId, token]);

  if (loading || !data || data.kept_count === 0) return null;

  const c = {
    cardBg: adminDarkMode ? '#0f172a' : 'white',
    border: adminDarkMode ? '#1e293b' : '#e2e8f0',
    text: adminDarkMode ? 'white' : '#1e293b',
    muted: adminDarkMode ? '#94a3b8' : '#64748b',
    faint: adminDarkMode ? '#64748b' : '#94a3b8',
  };

  return (
    <div
      className="mt-6 p-6 rounded-xl border"
      style={{ backgroundColor: c.cardBg, borderColor: c.border }}
      data-testid="keepsakes-panel"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium flex items-center gap-2" style={{ color: c.text }}>
          <Heart className="h-4 w-4" style={{ color: c.muted }} />
          Saved moments
        </h3>
        <span className="text-xs" style={{ color: c.faint }}>{data.kept_count} {data.kept_count === 1 ? 'item' : 'items'}</span>
      </div>
      <p className="text-[12px] mb-4" style={{ color: c.muted }}>
        What you held on to from the morning reveal. Archived for you.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <a
          href={`${API}/api/reveal/${data.reveal_token}/book`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium transition-opacity hover:opacity-75"
          style={{ color: '#7c3aed' }}
          data-testid="keepsakes-download-book"
        >
          Download book &rarr;
        </a>
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-xs transition-opacity hover:opacity-75"
          style={{ color: c.muted }}
          data-testid="keepsakes-toggle-list"
        >
          {expanded ? 'Hide list' : 'View list'}
        </button>
      </div>

      {expanded && (
        <div className="space-y-2">
          {data.items.map((entry, i) => (
            <div
              key={`${entry.kind}-${entry.item.id}`}
              className="flex items-start gap-3 text-[13px] py-2 border-t"
              style={{ borderColor: c.border, color: c.text }}
            >
              <span className="text-xs mt-0.5" style={{ color: c.faint, minWidth: '18px' }}>{i + 1}</span>
              {entry.kind === 'photo' && (
                <>
                  <img
                    src={`${API}${entry.item.image_url}`}
                    alt=""
                    className="w-10 h-10 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="truncate italic" style={{ color: c.muted }}>
                      {entry.item.note ? `"${entry.item.note}"` : 'Photo'}
                    </p>
                    <p className="text-[11px]" style={{ color: c.faint }}>from {entry.item.guest_name}</p>
                  </div>
                </>
              )}
              {entry.kind === 'note' && (
                <>
                  <MessageIcon color={c.muted} />
                  <div className="flex-1 min-w-0">
                    <p className="italic leading-snug" style={{ color: c.text }}>&ldquo;{entry.item.message}&rdquo;</p>
                    <p className="text-[11px] mt-0.5" style={{ color: c.faint }}>&mdash; {entry.item.guest_name}</p>
                  </div>
                </>
              )}
              {entry.kind === 'voice' && (
                <>
                  <VoiceBadge color={c.muted} />
                  <div className="flex-1 min-w-0">
                    <p className="italic" style={{ color: c.text }}>In {entry.item.guest_name}&rsquo;s voice</p>
                    <p className="text-[11px]" style={{ color: c.faint, fontVariantNumeric: 'tabular-nums' }}>
                      {Math.floor(entry.item.duration_sec / 60)}:{String(entry.item.duration_sec % 60).padStart(2, '0')}
                    </p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
      <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-5l-4 3v-3H6a2 2 0 0 1-2-2V6z" stroke={color} strokeWidth="1.3" />
    </svg>
  );
}

function VoiceBadge({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
      <rect x="9" y="3" width="6" height="13" rx="3" stroke={color} strokeWidth="1.3" />
      <path d="M6 11a6 6 0 0 0 12 0M12 19v2" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
